import type { Database } from '../../types/database';

export const SNAPSHOT_KEY = 'contract_group_database';
// A week covers a weekend away: the last data draws at once and /database
// refreshes behind it, rather than skeletons on the first open of the week.
export const SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
interface Snapshot { ownerId: string; cachedAt: number; data: Database }

// Both persisted identities must agree before any account data is restored.
export function storedSnapshotOwner(): string | null {
  try {
    const session = JSON.parse(localStorage.getItem('contract-group-auth') || 'null');
    const user = JSON.parse(localStorage.getItem('auth_user') || 'null');
    const uid = session?.user?.id;
    return typeof uid === 'string' && uid === user?.id ? uid : null;
  } catch { return null; }
}
export function validSnapshot(value: unknown, ownerId: string | null, version: string): Database | null {
  const snapshot = value as Snapshot | null;
  return ownerId && snapshot?.ownerId === ownerId && snapshot.data?.version === version
    && typeof snapshot.cachedAt === 'number' && snapshot.cachedAt <= Date.now()
    && Date.now() - snapshot.cachedAt < SNAPSHOT_TTL_MS ? snapshot.data : null;
}

let cacheGeneration = 0;
async function snapshotStore(mode: IDBTransactionMode, value?: Snapshot | null, generation = cacheGeneration): Promise<unknown> {
  if (typeof indexedDB === 'undefined') return null;
  return new Promise((resolve, reject) => {
    let expired = false;
    let active: IDBTransaction | null = null;
    const deadline = setTimeout(() => {
      expired = true;
      try { active?.abort(); } catch { /* already completed */ }
      reject(new Error('Snapshot storage timed out'));
    }, 1_500);
    const finish = (result: unknown) => { clearTimeout(deadline); resolve(result); };
    const fail = (error: unknown) => { clearTimeout(deadline); reject(error); };
    let open: IDBOpenDBRequest;
    try { open = indexedDB.open('quantis-account-snapshot', 1); }
    catch (error) { fail(error); return; }
    open.onupgradeneeded = () => open.result.createObjectStore('snapshots');
    open.onerror = () => fail(open.error);
    open.onsuccess = () => {
      const db = open.result;
      if (expired || (mode === 'readwrite' && generation !== cacheGeneration)) {
        db.close(); finish(null); return;
      }
      try {
        const transaction = db.transaction('snapshots', mode);
        active = transaction;
        const store = transaction.objectStore('snapshots');
        const request = mode === 'readonly' ? store.get('current')
          : value ? store.put(value, 'current') : store.delete('current');
        transaction.oncomplete = () => { db.close(); finish(request.result); };
        transaction.onerror = () => { db.close(); fail(transaction.error); };
        transaction.onabort = () => { db.close(); fail(transaction.error); };
      } catch (error) { db.close(); fail(error); }
    };
  });
}
export async function restoreSnapshot(ownerId: string | null, version: string): Promise<Database | null> {
  if (!ownerId) return null;
  let durable: Snapshot | null = null;
  try { durable = await snapshotStore('readonly') as Snapshot | null; } catch { /* unavailable */ }
  let synchronous: Snapshot | null = null;
  try { synchronous = JSON.parse(localStorage.getItem(SNAPSHOT_KEY) || 'null'); } catch { /* unavailable */ }
  const durableData = validSnapshot(durable, ownerId, version);
  const synchronousData = validSnapshot(synchronous, ownerId, version);
  // A reload can happen before the queued durable write commits. Never replace
  // the newer complete synchronous snapshot with an older IndexedDB copy.
  if (synchronousData && (!durableData || synchronous!.cachedAt >= durable!.cachedAt)) return synchronousData;
  return durableData;
}
let writes: Promise<unknown> = Promise.resolve();
function queueWrite(value: Snapshot | null): Promise<unknown> {
  const generation = cacheGeneration;
  const next = writes.then(() => snapshotStore('readwrite', value, generation));
  writes = next.catch(() => {});
  return next;
}
export async function persistSnapshot(data: Database, ownerId: string): Promise<void> {
  const generation = cacheGeneration;
  const snapshot: Snapshot = { ownerId, cachedAt: Date.now(), data: structuredClone(data) };
  if (generation === cacheGeneration && storedSnapshotOwner() === ownerId) {
    try {
      const serialized = JSON.stringify(snapshot);
      // Keep only complete small snapshots in synchronous storage; never trim collections.
      if (serialized.length <= 500_000) localStorage.setItem(SNAPSHOT_KEY, serialized);
      else localStorage.removeItem(SNAPSHOT_KEY);
    } catch { /* IndexedDB remains available when localStorage is full. */ }
  }
  // Publish the small synchronous copy before awaiting the durable queue, so
  // an immediate reload does not have to wait for another transaction.
  if (generation === cacheGeneration) await queueWrite(snapshot).catch(() => {});
}
export function clearSnapshot(): void {
  cacheGeneration++;
  try { localStorage.removeItem(SNAPSHOT_KEY); } catch { /* unavailable */ }
  void queueWrite(null).catch(() => {});
}
